import type { Locale } from './config'
import type { Namespace } from './messages'

/**
 * The namespaces a lazy client island can load
 * (`components/i18n/lazy-messages.tsx`): on a marketing page, the app
 * catalogs of the product scenes it plays; in the product, the onboarding
 * tours' words, which only an open tour's card needs
 * (`components/onboarding/onboarding-dock.tsx`). Each one is served as JSON,
 * English merged under the locale, by
 * `app/i18n/islands/[locale]/[namespace]/route.ts`; an island that needs
 * another namespace adds it here.
 */
export const ISLAND_NAMESPACES = [
  'analytics',
  'appShell',
  'audience',
  'automations',
  'billing',
  'brand',
  'chat',
  'domains',
  'emailEditor',
  'emails',
  'integrations',
  'onboardingTours',
  'productDemo',
  'settings',
  'templates',
  'triggers',
] as const satisfies ReadonlyArray<Namespace>

/** A namespace a lazy island can load (`ISLAND_NAMESPACES`). */
export type IslandNamespace = (typeof ISLAND_NAMESPACES)[number]

const ISLAND_NAMESPACE_SET: ReadonlySet<string> = new Set(ISLAND_NAMESPACES)

export function isIslandNamespace(value: string): value is IslandNamespace {
  return ISLAND_NAMESPACE_SET.has(value)
}

/** Where the island catalog of `namespace` in `locale` is served. */
export function islandMessagesPath(
  locale: Locale,
  namespace: IslandNamespace
): string {
  return `/i18n/islands/${locale}/${namespace}`
}
